import { useState } from "react";
import { useForm } from "react-hook-form";
import Head from "next/head";
import { CardElement, useStripe, useElements, CardElementProps } from "@stripe/react-stripe-js";
import { registerPlugin } from "react-filepond";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginFileEncode from "filepond-plugin-file-encode";
import { fromUnixTime, format, getUnixTime, parse } from "date-fns";
import ScrollContainer from "react-perfect-scrollbar";
import { WithStripeElement } from "~/hoc/WithStripeElement";
import { useUser } from "~/context/userContext";
import { useToast } from "~/utils";
import { FormSubmitButton } from "~/components/FormSubmitButton";
import { NeedReauthenticateModal } from "~/components/NeedReauthenticateModal";
import { PageTitle } from "~/components/aud/ui/PageTitle";
import { Main } from "~/components/aud/base/Main";
import { Section, SectionTitle } from "~/components/aud/base/Section";
import { Content } from "~/components/aud/base/Content";
import { ChangePasswordForm } from "~/components/settings/ChangePasswordForm";
import { WithdrawalContent } from "~/components/settings/WithdrawalContent";
import { ReadOnlyBirthdayField } from "~/components/settings/ReadOnlyBirthdayField";
import { BirthdayField } from "~/components/settings/BirthdayField";
import { EmailField } from "~/components/settings/EmailField";
import { SubscriptionField } from "~/components/settings/SubscriptionField";
import { UpdateSubscriptionPayment } from "~/components/subscriptions/SelectSubscriptionPayment";
import { Analytics } from "~/components/GoogleAnalytics";
import { Language, UpdateMeInput } from "~/generated/rtk-query/nowdo-live-api.generated";
import { SetEmailAndPasswordForm } from "~/components/settings/SetEmailAndPasswordForm";
import { isNullish } from "~/submodules/isNullish";
import Link from "next/link";
import { getInitialPageProps } from "~/store";
import {
  nowdoApi,
  useGetMeQuery,
  useGetMeStripeCardQuery,
  useUpdateMeMutation,
  useUpdateSubscriptionMutation,
} from "~/hooks/rtk-query-api";
import { NextPage } from "next";
import css from "./settings.module.scss";

const SettingsPage: NextPage = WithStripeElement(() => {
  const { data: me } = useGetMeQuery();
  const toast = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: me?.email,
      language: me?.language,
      birth_date: format(fromUnixTime(me?.birthDate || getUnixTime(new Date())), "yyyy-MM-dd"),
    },
  });
  const { user } = useUser();
  const stripe = useStripe();
  const elements = useElements();
  const [updateMe] = useUpdateMeMutation();
  const [updateSubscription] = useUpdateSubscriptionMutation();
  const [showUpdateSubscriptionPayment, setShowUpdateSubscriptionPayment] = useState(false);

  const [isChangedEmail, setIsChangedEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFilledPaymentMethod, setIsFilledPaymentMethod] = useState(false);

  const hasEmailAndPasswordUser = Boolean(user && user.email !== "");
  // TODO: always need reauthenticate when entering settings page ATM.
  const [needReauthenticate, setNeedReauthenticate] = useState(hasEmailAndPasswordUser);

  const [files, setFiles] = useState([]);

  registerPlugin(
    FilePondPluginImagePreview,
    FilePondPluginFileValidateSize,
    FilePondPluginFileValidateType,
    FilePondPluginFileEncode
  );

  const getMeStripeCard = useGetMeStripeCardQuery(undefined, {
    skip: user?.isPartnerSchoolStudent || !user?.isSubscribed,
  });

  const onChangePaymentMethod: Exclude<CardElementProps["onChange"], undefined> = (event) => {
    setIsFilledPaymentMethod(event.complete);
  };

  const onSubmitAccount = async (data: { email: string; language: Language; birthDate: string; plan: string }) => {
    setLoading(true);
    try {
      if (isNullish(user) || isNullish(stripe)) {
        toast.error("エラーが発生しました。お手数ですがもう一度時間を開けておためしください。");
        return;
      }

      if (isFilledPaymentMethod) {
        const card = elements?.getElement(CardElement);
        if (isNullish(card)) {
          toast.error("エラーが発生しました。お手数ですがもう一度時間を開けておためしください。");
          return;
        }
        const paymentMethodResponse = await stripe.createPaymentMethod({
          type: "card",
          card,
        });
        if (paymentMethodResponse?.error) {
          throw paymentMethodResponse.error;
        }

        const response = await updateSubscription({
          updateSubscriptionInput: {
            paymentMethodId: paymentMethodResponse.paymentMethod.id,
          },
        }).unwrap();

        if (response.status === "active" || response.status === "trialing") {
          toast.success("決済情報の更新に成功しました。");
          Analytics({
            action: "click_update_subscription_success",
            label: "Subscription/update",
            user,
          });
        }
      }

      const input: UpdateMeInput = {};

      if (user.email !== data.email) {
        input.email = data.email;
      }

      if (data.birthDate) {
        const parsedDate = parse(data.birthDate, "yyyy-MM-dd", new Date());
        input.birthDate = getUnixTime(parsedDate);
      }

      if (files.length > 0) {
        input.idCardImage = (files[0] as any).getFileEncodeDataURL();
      }

      if (!Object.keys(input).length) {
        return;
      }

      await updateMe({ updateMeInput: input }).unwrap();

      toast.success("アカウント情報を更新しました。");

      if (user.email !== input.email) {
        setIsChangedEmail(true);
      }
    } catch (e) {
      if (e.data?.error && (e.status === 422 || e.status === 409)) {
        toast.error(
          e.response?.data?.error ?? "エラーが発生しました。お手数ですがもう一度時間を開けておためしください。"
        );
      } else {
        toast.error("エラーが発生しました。お手数ですがもう一度時間を開けておためしください。");
      }
    } finally {
      setLoading(false);
    }
  };

  if (isNullish(me)) {
    return null;
  }

  const needIDResubmit = me && me.state == "need_resubmit";

  return (
    <Main nav="account_settings">
      <Head>
        <title>アカウント設定</title>
      </Head>
      <Section>
        <PageTitle title="Settings" sub="アカウント設定" />
        {isChangedEmail && <RequiredLoginAgain />}
        {needReauthenticate && <NeedReauthenticateModal user={user} onFinish={() => setNeedReauthenticate(false)} />}
        <form onSubmit={handleSubmit(onSubmitAccount as any)}>
          <Content>
            <SectionTitle>アカウント情報</SectionTitle>
            {needIDResubmit && <span className="sub red">身分証明書の再提出が必要です。</span>}
            <div className="_form">
              <SubscriptionField me={me} />
              {hasEmailAndPasswordUser && <EmailField register={register} errors={errors} />}
              {needIDResubmit ? (
                <BirthdayField files={files} setFiles={setFiles} register={register} errors={errors} />
              ) : (
                <ReadOnlyBirthdayField birth_date={me.birthDate} />
              )}
              {getMeStripeCard.data && !showUpdateSubscriptionPayment && (
                <Content>
                  <div className={css.card}>
                    <span className="label">クレジットカード番号</span>
                    <div className="field">
                      <p className={css.cardNumber}>**** **** **** {getMeStripeCard.data.last4}</p>
                      <span className="sub">
                        有効期限 {getMeStripeCard.data.expYear}年{getMeStripeCard.data.expMonth}月
                      </span>
                      <button className="sub red" onClick={() => setShowUpdateSubscriptionPayment(true)}>
                        変更する
                      </button>
                    </div>
                  </div>
                </Content>
              )}
              {showUpdateSubscriptionPayment && (
                <UpdateSubscriptionPayment onChange={onChangePaymentMethod} theme={"light"} />
              )}
            </div>
            {(!user?.isPartnerSchoolStudent || hasEmailAndPasswordUser) && (
              <FormSubmitButton size="medium" loading={loading}>
                アカウント情報更新
              </FormSubmitButton>
            )}
          </Content>
        </form>
        {!hasEmailAndPasswordUser && <SetEmailAndPasswordForm />}
        {hasEmailAndPasswordUser && <ChangePasswordForm user={me} />}
        {user && !user.isPartnerSchoolStudent && <WithdrawalContent />}
      </Section>
    </Main>
  );
});

SettingsPage.getInitialProps = getInitialPageProps((store) => async () => {
  const { data } = await store.dispatch(nowdoApi.endpoints.getMe.initiate());
  return {
    me: data,
  };
});

export default SettingsPage;

const RequiredLoginAgain = () => (
  <div className="_modal__wrapper show">
    <ScrollContainer>
      <div>
        <div className="_modal">
          <div className="header">再ログイン</div>
          <div className="content">
            <p>メールアドレスの変更に成功しました。変更後のメールアドレスで再度ログインしてください。</p>
          </div>
          <div className="footer right">
            <Link href="/signin" className="_button primary">
              ログインし直す
            </Link>
          </div>
        </div>
      </div>
    </ScrollContainer>
  </div>
);
